/**
 * The day cover, first in every day (the Oct 21 canvas, "Proposed · p.1 · Day
 * cover + route QR"): "Today's Run" with the day in its date stamp, the day in
 * one line under it, and the route's one code top right (it replaces the
 * separate Delivery Route page). Then a box for food that rides along for a
 * later day, the stops in delivery order with their window and every section
 * each one takes (Breakfast, Lunch, PM snack, Milk, Supplies, Condiments), and
 * what is in the packet, page by page.
 *
 * The table of contents is filled in once the day's other sections exist
 * (`withContents`), so it names only what printed. Pure; unit-tested.
 */

import { clockLabel } from '@/lib/chefs/delivery-route'
import { type Mealtime, MEALTIME_LABEL, MEALTIME_ORDER } from '@/lib/chefs/types'

import { KITCHEN_COPY, type PacketDay } from './day'
import { campusMealSections, serveDayShort, serveWeekday } from './meals'
import { isCondimentDeliverable } from './pull-pack'
import { routeCode } from './route'
import type { PacketStop } from './stops'
import { stopListWords } from './summaries'
import type { PacketSection, PacketSectionKind, PacketUnit } from './units'

export function coverSectionId(dayIso: string): string {
  return `${dayIso}|cover`
}

/** "Breakfast", "PM snack": a meal as the cover's table says it. */
function mealWord(mealtime: Mealtime): string {
  return MEALTIME_LABEL[mealtime].replace(' Snack', ' snack')
}

/** "Mon 10/19" for a later serve day. */
function laterDay(dayKey: string): string {
  return `${serveWeekday(dayKey)} ${serveDayShort(dayKey).split(' ')[1]}`
}

/**
 * Every section a stop takes, in the order its sheet and the pull & pack list print them: its meals in mealtime
 * order (a later day's says the day, "Breakfast (Mon 10/19)"), then Milk, Supplies and Condiments where it takes them.
 */
export function stopSections(stop: Pick<PacketStop, 'campus'>, deliveryIso: string): string[] {
  const campus = stop.campus
  const meals = campusMealSections(campus, deliveryIso)
    .slice()
    .sort((a, b) => MEALTIME_ORDER.indexOf(a.mealtime) - MEALTIME_ORDER.indexOf(b.mealtime))
    .map((section) =>
      section.serve.later && section.serve.laterDay
        ? `${mealWord(section.mealtime)} (${laterDay(section.serve.laterDay)})`
        : mealWord(section.mealtime),
    )
  const deliverables = campus.mealtimes.flatMap((block) => block.deliverables)
  return [
    ...new Set(meals),
    ...((campus.milk ?? []).length > 0 ? ['Milk'] : []),
    ...(campus.supplies.length > 0 || deliverables.some((d) => (d.supplies ?? []).length > 0) ? ['Supplies'] : []),
    ...(deliverables.some(isCondimentDeliverable) ? ['Condiments'] : []),
  ]
}

/** The first driven stop's window opening, "7:00 AM"; null when the route says none. */
function firstWindow(day: PacketDay): string | null {
  for (const stop of day.stops) {
    const start = clockLabel(stop.route?.window?.start)
    if (start) {
      return start
    }
  }
  return null
}

/** "5 stops · first window 7:00 AM · 12 prep recipes · 14 pull items". */
export function coverLine(day: PacketDay, recipes: number, pullItems: number): string {
  const window = firstWindow(day)
  return [
    `${day.stops.length} ${day.stops.length === 1 ? 'stop' : 'stops'}`,
    window ? `first window ${window}` : null,
    `${recipes} prep ${recipes === 1 ? 'recipe' : 'recipes'}`,
    `${pullItems} pull ${pullItems === 1 ? 'item' : 'items'}`,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * Food that leaves today for a later day, a line per meal and day: "Mon 10/19 breakfast goes out today for stops 1
 * and 2 (352 servings). Pack and label it as a separate group."
 */
export function rideAlongLines(day: Pick<PacketDay, 'stops' | 'iso'>): string[] {
  const meals = new Map<string, { words: string; stops: string[]; servings: number }>()
  for (const stop of day.stops) {
    for (const section of campusMealSections(stop.campus, day.iso)) {
      if (!section.serve.later || !section.serve.laterDay) {
        continue
      }
      const key = `${section.serve.laterDay}|${section.mealtime}`
      const meal = meals.get(key) ?? {
        words: `${laterDay(section.serve.laterDay)} ${MEALTIME_LABEL[section.mealtime].toLowerCase()}`,
        stops: [],
        servings: 0,
      }
      meal.stops.push(stop.identity.number)
      meal.servings += section.block.meals
      meals.set(key, meal)
    }
  }
  return [...meals.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(
      ([, meal]) =>
        `${meal.words} goes out today for ${stopListWords(meal.stops).toLowerCase()} (${meal.servings.toLocaleString()} ${meal.servings === 1 ? 'serving' : 'servings'}). Pack and label it as a separate group.`,
    )
}

/**
 * The special-diet meals going out today, in one line (the Oct 21 mockup's cover): each meal for each diet with its
 * total and the stops that take it, "1 Southwest Halal Chicken Bowl (no peanut, gluten free) for stop 3 · 2 Cheese
 * Pizza (gluten free) for stops 1 and 2". Null when there are none.
 */
export function specialDietLine(day: Pick<PacketDay, 'stops'>): string | null {
  const meals = new Map<string, { words: string; quantity: number; stops: string[] }>()
  for (const stop of day.stops) {
    for (const item of stop.campus.mealtimes.flatMap((block) => block.specialDiets ?? [])) {
      const key = `${item.name}|${item.restrictions.join(',')}`
      const words = item.restrictions.length > 0 ? `${item.name} (${item.restrictions.join(', ')})` : item.name
      const meal = meals.get(key) ?? { words, quantity: 0, stops: [] }
      meal.quantity += item.quantity
      if (!meal.stops.includes(stop.identity.number)) {
        meal.stops.push(stop.identity.number)
      }
      meals.set(key, meal)
    }
  }
  if (meals.size === 0) {
    return null
  }
  return `${[...meals.values()]
    .map((meal) => `${meal.quantity.toLocaleString()} ${meal.words} for ${stopListWords(meal.stops).toLowerCase()}`)
    .join(' · ')}.`
}

/** The cover for one delivery day, before its table of contents. */
export function coverSection(
  day: PacketDay,
  origin: string | null,
  counts: { recipes: number; pullItems: number },
): PacketSection {
  const sid = coverSectionId(day.iso)
  const specialDiets = specialDietLine(day)
  const units: PacketUnit[] = [
    ...rideAlongLines(day).map(
      (text, i): PacketUnit => ({
        id: `${sid}|notice|${i}`,
        group: null,
        role: 'block',
        kind: 'cover-notice',
        label: 'Ride-along',
        text,
      }),
    ),
    ...(specialDiets
      ? [
          {
            id: `${sid}|notice|special-diets`,
            group: null,
            role: 'block',
            kind: 'cover-notice',
            label: 'Special diets',
            text: specialDiets,
          } satisfies PacketUnit,
        ]
      : []),
  ]

  const stopsGroup = `${sid}|stops`
  units.push(
    {
      id: `${stopsGroup}|band`,
      group: stopsGroup,
      role: 'band',
      kind: 'band',
      text: 'STOPS IN DELIVERY ORDER',
      note: null,
      level: 1,
      space: 'group',
    },
    { id: `${stopsGroup}|head`, group: stopsGroup, role: 'band', kind: 'cover-stops-head' },
    ...day.stops.map(
      (stop): PacketUnit => ({
        id: `${stopsGroup}|${stop.identity.number}`,
        group: stopsGroup,
        role: 'row',
        kind: 'cover-stop',
        stop: stop.identity,
        window: stop.window,
        windowNote: stop.windowNote,
        sections: stopSections(stop, day.iso),
        warning: stop.warning,
      }),
    ),
  )

  return {
    id: sid,
    kind: 'cover',
    dayIso: day.iso,
    band: { name: 'DAY COVER', counter: null, right: KITCHEN_COPY, style: 'solid' },
    title: {
      eyebrow: 'KITCHEN PACKET · DAY COVER',
      title: 'Today’s Run',
      subtitle: coverLine(day, counts.recipes, counts.pullItems),
      note: null,
      dateBox: day.dateBox,
      code: routeCode(day.routeDay, origin),
    },
    footer: { key: `${day.iso}|cover`, label: 'Day Cover', counted: false },
    units,
    repeat: [],
    preferOnePage: true,
  }
}

/** The table of contents' entries, in print order: each kind of page the day printed, under its footer's name. */
const CONTENTS: ReadonlyArray<{ kinds: PacketSectionKind[]; label: (count: number) => string }> = [
  { kinds: ['shopping-list'], label: () => 'Shopping List' },
  { kinds: ['production-summary'], label: () => 'Production Totals' },
  { kinds: ['recipe'], label: (count) => `Prep Recipes (${count})` },
  { kinds: ['pull-pack'], label: () => 'Pull & Pack List' },
  { kinds: ['stop-summary'], label: () => 'Stop Sheets · Kitchen + School Copy' },
]

/** The cover with "In this packet": a row per kind of page among `sections`, its pages filled in at print. */
export function withContents(cover: PacketSection, sections: readonly PacketSection[]): PacketSection {
  const group = `${cover.id}|contents`
  const rows = CONTENTS.flatMap(({ kinds, label }, i): PacketUnit[] => {
    const matching = sections.filter((section) => kinds.includes(section.kind))
    if (matching.length === 0) {
      return []
    }
    // A pull list of 6+ stops is titled "Pull List": the entry says the page's own title.
    const named = matching[0]!.kind === 'pull-pack' && matching[0]!.title ? matching[0]!.title.title : null
    return [
      {
        id: `${group}|${i}`,
        group,
        role: 'row',
        kind: 'cover-toc',
        label: named ?? label(matching.length),
        sectionIds: matching.map((section) => section.id),
      },
    ]
  })
  if (rows.length === 0) {
    return cover
  }
  return {
    ...cover,
    units: [
      ...cover.units,
      {
        id: `${group}|band`,
        group,
        role: 'band',
        kind: 'band',
        text: 'IN THIS PACKET',
        note: null,
        level: 1,
        space: 'group',
      },
      ...rows,
    ],
  }
}
